// Airwallex Components SDK (@airwallex/components-sdk 1.35.2).
// init() loads the SDK scripts and must be called once per application; createElement() returns a Promise
// with the element (mount / on / destroy).
import { init, createElement } from '@airwallex/components-sdk';
import type { Payment } from '@airwallex/components-sdk';
import { TDropInEnv } from 'services/api/paymentMethods/types';

let initPromise: Promise<unknown> | null = null;

export const ensureAirwallex = (env: TDropInEnv) => {
	initPromise ??= init({ env, enabledElements: ['payments'], locale: 'en' }).catch((error) => {
		// let the next attempt try again (e.g. a network error while loading the SDK)
		initPromise = null;
		throw error;
	});
	return initPromise;
};

// Apple Pay JS SDK: the same script (and SRI hash) that the Airwallex Drop-in loads itself. In Safari Apple Pay is built in;
// in Chrome / Edge / Firefox the SDK provides ApplePaySession and shows a code to scan with an iPhone. The Drop-in reuses an
// already loaded script (it looks for script[src*="apple-pay-sdk.js"]).
const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/v1.3.2/apple-pay-sdk.js';
const APPLE_PAY_SDK_INTEGRITY =
	'sha384-DZRWMZLyVXr+7shJfal8pIG2v4KisLoSWFjZQMUv0+GWaCwoa82qeHsWrbBIUDPU';
const APPLE_PAY_SDK_WAIT_MS = 8000;

let applePaySdkPromise: Promise<boolean> | null = null;

export const canUseApplePay = (): boolean => {
	try {
		const session = (window as unknown as { ApplePaySession?: { canMakePayments?: () => boolean } })
			.ApplePaySession;
		return !!session?.canMakePayments?.();
	} catch {
		return false;
	}
};

/** Loads the Apple Pay JS SDK once (resolves false if it could not be loaded in time). */
export const loadApplePaySdk = (): Promise<boolean> => {
	applePaySdkPromise ??= new Promise<boolean>((resolve) => {
		let settled = false;
		const done = (loaded: boolean) => {
			if (settled) return;
			settled = true;
			if (!loaded) applePaySdkPromise = null; // a later dialog may try again
			resolve(loaded);
		};
		window.setTimeout(() => done(false), APPLE_PAY_SDK_WAIT_MS);

		const existing = document.querySelector<HTMLScriptElement>('script[src*="apple-pay-sdk.js"]');
		if (existing) {
			if ('ApplePaySession' in window) {
				done(true);
				return;
			}
			existing.addEventListener('load', () => done(true));
			existing.addEventListener('error', () => done(false));
			return;
		}

		const script = document.createElement('script');
		script.src = APPLE_PAY_SDK_URL;
		script.integrity = APPLE_PAY_SDK_INTEGRITY;
		script.crossOrigin = 'anonymous';
		script.async = true;
		script.addEventListener('load', () => done(true));
		script.addEventListener('error', () => done(false));
		document.head.appendChild(script);
	});
	return applePaySdkPromise;
};

export type TDropInElement = Payment.DropInElementType;
export type TDropInOptions = Payment.DropInElementOptions;

/**
 * 3-D Secure window size. Without it the SDK opens the challenge full screen (it looks like a different page);
 * with it the challenge is a centered popup over the dimmed page. 500×600 is the standard EMV 3DS challenge size,
 * reduced on small screens (the SDK adds 54 px for its header).
 */
export const threeDsPopupSize = (): Payment.PopUpStyle => ({
	popupWidth: Math.max(280, Math.min(500, window.innerWidth - 32)),
	popupHeight: Math.max(400, Math.min(600, window.innerHeight - 54 - 32)),
});

export { createElement };
